import { FormattedCareType } from '@/apps/provider/profile/__types__/CareTypes';

export const CHILD_CARE_EDIT_ROUTE_TYPES = ['babysitter', 'nanny'] as const;

export type ChildCareEditRouteType = (typeof CHILD_CARE_EDIT_ROUTE_TYPES)[number];

export const careTypeRouteToGraphqlKey = {
  babysitter: 'babysitter',
  nanny: 'nanny',
  pet_care: 'petCare',
  housekeeping: 'housekeeping',
  companion_care: 'companionCare',
} as const;

export type ProfileEditRouteCareType = keyof typeof careTypeRouteToGraphqlKey;

export type ProfileEditGraphqlCareType = (typeof careTypeRouteToGraphqlKey)[ProfileEditRouteCareType];

export const isChildCareEditRoute = (careType: string): careType is ChildCareEditRouteType =>
  CHILD_CARE_EDIT_ROUTE_TYPES.includes(careType as ChildCareEditRouteType);

export const SUPPORTED_PROFILE_EDIT_CARE_TYPES = ['housekeeping', 'pet_care', 'babysitter', 'companion_care', 'nanny'] as const;

export const isSupportedProfileEditCareType = (careType: string): careType is ProfileEditRouteCareType =>
  (SUPPORTED_PROFILE_EDIT_CARE_TYPES as readonly string[]).includes(careType);

export type ProfileEditSection =
  | 'bio'
  | 'rates'
  | 'experience'
  | 'availability'
  | 'education'
  | 'specialty-skills'
  | 'services'
  | 'additional-services'
  | 'special-needs'
  | 'homework-help'
  | 'certifications';

// Which edit sections each care type exposes. Used by the form dispatcher and to
// decide whether the profile view should deep-link into the SPA edit forms.
export const CARE_TYPE_EDIT_SECTIONS: Record<ProfileEditGraphqlCareType, ProfileEditSection[]> = {
  babysitter: [
    'bio',
    'rates',
    'experience',
    'education',
    'specialty-skills',
    'services',
    'additional-services',
    'certifications',
    'special-needs',
    'homework-help',
  ],
  nanny: [
    'bio',
    'rates',
    'experience',
    'availability',
    'education',
    'specialty-skills',
    'services',
    'additional-services',
    'certifications',
    'special-needs',
    'homework-help',
  ],
  companionCare: [
    'bio',
    'rates',
    'experience',
    'education',
    'specialty-skills',
    'services',
    'additional-services',
    'certifications',
  ],
  petCare: ['bio', 'rates', 'experience', 'availability', 'education', 'specialty-skills'],
  housekeeping: ['bio', 'rates', 'experience', 'availability', 'education', 'specialty-skills', 'services'],
};

export const routeCareTypeToFormattedCareType = (careType: string): FormattedCareType => {
  const map: Record<string, FormattedCareType> = {
    babysitter: FormattedCareType.BABYSITTER,
    nanny: FormattedCareType.NANNY,
    pet_care: FormattedCareType.PET_CARE,
    housekeeping: FormattedCareType.HOUSEKEEPING,
    companion_care: FormattedCareType.COMPANION_CARE,
  };

  return map[careType] || FormattedCareType.BABYSITTER;
};

/**
 * Get the GraphQL key for a route care type
 */
export const getCareTypeKey = (careType: ProfileEditRouteCareType): ProfileEditGraphqlCareType =>
  careTypeRouteToGraphqlKey[careType];

/**
 * Get the SPA edit path for a care type and section
 */
export const getProfileEditSectionPath = (careType: string, section: string): string =>
  `/provider/profile/${careType}/edit/${section}`;

/**
 * Whether a route care type exposes the given edit section in the SPA forms.
 */
export const careTypeSupportsEditSection = (careType: string, section: string): boolean => {
  if (!isSupportedProfileEditCareType(careType)) return false;
  const graphqlKey = careTypeRouteToGraphqlKey[careType as keyof typeof careTypeRouteToGraphqlKey];
  return CARE_TYPE_EDIT_SECTIONS[graphqlKey].includes(section as ProfileEditSection);
};
